<template>
  <content-block class="content-edit-price" :required="true">
    <div slot="instructions">
      <h5>{{ $t("Pricing Options") }}</h5>
      <i18n
        tag="p"
        path='Pick which pricing options you want locations to have access to. Monthly and yearly licenses are "floating" which allows locations to use any license on any station. After all available licenses are in use, each additional station is charged at the Per Minute rate.'
      >
      </i18n>
      <br>
      <i18n
        tag="p"
        path="You can learn more about pricing options by reading our {pricingGuide}."
      >

        <a
          class="link-style"
          href="https://support.springboardvr.com/hc/en-us/articles/360015797813-Pricing-Distribution-Guidelines"
          slot="pricingGuide"
          rel="noopener"
          target="_blank"
        >{{ $t('Pricing Guide') }}</a>
      </i18n>
    </div>

    <div class="price">
      <div class="price-group mb5">
        <div>
          <h5 class="f3 label">{{ $t("Per Minute License") }}</h5>
          <p class="f5">
            {{ $t('Adding a per minute license is required. Locations are charged for every minute they use your title.') }}
          </p>
        </div>
        <div>
          <UIInput
            :title="$t('Per Minute License Price in USD')"
            :value="formattedPriceMinute"
            min="0"
            max="0.99"
            @input="updateBasePrice('basePriceMinute', $event.target.value, $event)"
            required
          />
          <div class="instructions-grid">
            <template v-if="!removeMinuteInstructions">
              <UIIcon class="alert-triangle flex self-start" name="alert-triangle-fill" />
              <small class="minute-instructions warning"> {{ minuteInstructions }}</small>
            </template>
            <template v-else>
              <small class="minute-instructions">{{ $t('Enter a dollar amount between 0 and 0.99. We recommend $0.06 USD per minute or less.') }}</small>
            </template>
          </div>
        </div>
      </div>
      <div class="monthly-pricing price-group" v-if="!contentOwnership.disablePriceMonth">
        <div>
          <h5 class="f3 label">{{ $t("Monthly License") }}</h5>
          <p class="f5">
            {{ $t('Adding a monthly license is required. Locations can buy floating monthly licenses for a flat fee.') }}
          </p>
        </div>
        <div>
            <UIInput
              :title="$t('Monthly License Price in USD')"
              :value="formattedPriceMonth"
              min="0"
              max="0.99"
              @input="updateBasePrice('basePriceMonth', $event.target.value, $event)"
              required
            />
            <div class="instructions-grid" v-if="!removeMonthlyInstructions">
              <UIIcon class="alert-triangle flex self-start" name="alert-triangle-fill" />
              <small class="monthly-instructions warning"> {{ monthlyInstructions }}</small>
            </div>
        </div>
      </div>

      <div class="yearly-pricing pricing" v-if="!contentOwnership.disablePriceYear">
        <UISwitch
          :title="$t('Yearly License')"
          :instructions="$t('Adding a yearly license is optional. Locations can buy floating yearly licenses for a flat fee. Contact support@springboardvr.com if you have enabled yearly license and want to remove it.')"
          :value="contentOwnership.basePriceYear !== null"
          @change="updateContentOwnership('basePriceYear', getValueForYearly($event))"
          :disabled="yearlyPricingLock"
        />
        <div>
          <UIInput
            type="number"
            v-if="showMonthlyYearlyInputs('basePriceYear')"
            :title="$t('Yearly License Price in USD')"
            :value="formattedPriceYear"
            @input="updateBasePrice('basePriceYear', $event.target.value, $event)"
            required
          />
          <div class="instructions-grid" v-if="!removeYearlyInstructions">
            <UIIcon class="alert-triangle flex self-start" name="alert-triangle-fill" />
            <small class="yearly-instructions warning"> {{ yearlyInstructions }}</small>
          </div>
        </div>

      </div>

      <hr>

      <h1>{{ $t('Coming Soon') }}</h1>

      <div class="play-pricing pricing">
        <UISwitch
          :value="contentOwnership.basePricePlay !== null"
          :title="$t('Per Play License')"
          :instructions="$t('Coming soon: locations will have the option to license your content on a per play basis. Each time your title is launched and played for longer than the Minimum Play Time, the location will be charged the Per Play License Price. ')"
          @change="updateBasePricePlay"
        />
        <div>
          <UIInput
            type="number"
            class="price-play"
            min="0"
            max="25"
            v-if="contentOwnership.basePricePlay !== null"
            :value="formattedPricePlay"
            @input="updateBasePrice('basePricePlay', $event.target.value, $event)"
            :title="$t('Per Play Price in USD')"
            :instructions="$t('Enter a dollar amount between 0 and 25.')"
            required
          />
          <UIInput
            type="number"
            class="duration-input"
            v-if="contentOwnership.basePricePlay !== null"
            v-model="durationPlay"
            :title="$t('Minimum Play Time in Seconds')"
            :instructions="$t('To avoid locations getting billed for launching your content by mistake, we suggest that you make your content free for the first 60 seconds. This applies only to Per Play Pricing.')"
            required
          />
        </div>
      </div>
    </div>
  </content-block>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import ContentBlock from '@/core/shared/components/ContentBlock';
import UIInput from '@/core/shared/components/ui/UIInput';
import UISwitch from '@/core/shared/components/ui/UISwitch';
import UIRange from '@/core/shared/components/ui/UIRange';
import UIIcon from '@/core/shared/components/ui/UIIcon';
import { debounce } from 'lodash-es';
import moment from 'moment-timezone';

@Component({
  components: {
    ContentBlock,
    UIInput,
    UISwitch,
    UIRange,
    UIIcon,
  },
  methods: {
    updateBasePrice: debounce(function (priceType, value, event) {
      if (value && value >= 0) {
        this.updateContentOwnership(priceType, Math.floor(value * 100));

        if (priceType === 'basePriceMinute' && this.contentOwnership.minutePrice.current.price === Math.floor(value * 100)) {
          this.removeMinuteInstructions = true;
        } else if (priceType === 'basePriceMonth' && this.contentOwnership.monthlyPrice && this.contentOwnership.monthlyPrice.current && this.contentOwnership.monthlyPrice.current.price === Math.floor(value * 100)) {
          this.removeMonthlyInstructions = true;
        } else if (priceType === 'basePriceYear' && this.contentOwnership.yearlyPrice && this.contentOwnership.yearlyPrice.current && this.contentOwnership.yearlyPrice.current.price === Math.floor(value * 100)) {
          this.removeYearlyInstructions = true;
        }
      } else {
        this.updateContentOwnership(priceType, 'unset');
        setTimeout(() => {
          event.target.value = '';
        }, 0);
      }
    }, 500),
  },
  props: {
    contentOwnership: {
      type: Object,
      required: true,
    },
    yearlyPricingLock: { type: Boolean, required: true },
  },
  watch: {
    formattedPriceYear () {
      if (typeof this.localContentOwnership.basePriceYear === 'number' && this.localContentOwnership.basePriceYear < this.localContentOwnership.basePriceMonth) {
        this.$store.commit('setFlash', {
          type: 'error',
          message: 'Your yearly price can\'t be lower than your monthly price',
        });
        this.removeYearlyInstructions = true;
      }
    },
    formattedPriceMonth () {
      if (typeof this.localContentOwnership.basePriceYear === 'number' && this.localContentOwnership.basePriceYear < this.localContentOwnership.basePriceMonth) {
        this.$store.commit('setFlash', {
          type: 'error',
          message: 'Your yearly price can\'t be lower than your monthly price',
        });
        this.removeMonthlyInstructions = true;
      }
    },
  },
})
export default class BasePrice extends Vue {
  removeMinuteInstructions = null;
  removeMonthlyInstructions = null;
  removeYearlyInstructions = null;

  get minuteInstructions () {
    if (this.contentOwnership.minutePrice && this.contentOwnership.minutePrice.upcoming) {
      const date = moment.utc(this.contentOwnership.minutePrice.upcoming.startsAt).format('LL');
      const minutePrice = `$${(this.contentOwnership.minutePrice.current.price / 100).toFixed(2)}`;
      return this.$t('The price seen above will take effect on {date}. Until then, the price stays at {minutePrice} USD.', { date, minutePrice });
    }
  }

  get monthlyInstructions () {
    if (!this.contentOwnership.monthlyPrice.current) return;
    if (this.contentOwnership.monthlyPrice.upcoming) {
      const date = moment.utc(this.contentOwnership.monthlyPrice.upcoming.startsAt).format('LL');
      const monthlyPrice = `$${(this.contentOwnership.monthlyPrice.current.price / 100).toFixed(2)}`;
      return this.$t('The price seen above will take effect on {date}. Until then, the price stays at {monthlyPrice} USD.', { date, monthlyPrice });
    }
  }

  get yearlyInstructions () {
    if (!this.contentOwnership.yearlyPrice.current) return;
    if (this.contentOwnership.yearlyPrice.upcoming) {
      const date = moment.utc(this.contentOwnership.yearlyPrice.upcoming.startsAt).format('LL');
      const yearlyPrice = `$${(this.contentOwnership.yearlyPrice.current.price / 100).toFixed(2)}`;
      return this.$t('The price seen above will take effect on {date}. Until then, the price stays at {yearlyPrice} USD.', { date, yearlyPrice });
    }
  }

  get localContentOwnership () {
    return new Proxy(this.contentOwnership, {
      set: (object, property, value) => {
        this.$emit('edit:contentOwnership', property, value);
        return true;
      },
    });
  }

  get formattedPriceMinute () {
    if (typeof this.localContentOwnership.basePriceMinute === 'number') {
      return (this.localContentOwnership.basePriceMinute / 100).toFixed(2);
    }
    return null;
  }

  get formattedPriceMonth () {
    if (this.localContentOwnership.basePriceMinute > 0 && this.localContentOwnership.basePriceMonth === 0) {
      this.$store.commit('setFlash', {
        type: 'error',
        message: 'Your monthly price can\'t be set to $0.',
      });
      this.localContentOwnership.basePriceMonth = 'unset';
    } else if (typeof this.localContentOwnership.basePriceMonth === 'number') {
      return (this.localContentOwnership.basePriceMonth / 100).toFixed(2);
    }
  }

  get formattedPriceYear () {
    if (typeof this.localContentOwnership.basePriceYear === 'number') {
      return (this.localContentOwnership.basePriceYear / 100).toFixed(2);
    }
  }

  get formattedPricePlay () {
    return (this.localContentOwnership.basePricePlay / 100).toFixed(2);
  }

  get durationPlay () {
    return this.localContentOwnership.baseDurationPlay;
  }

  set durationPlay (value) {
    this.updateContentOwnership('baseDurationPlay', value);
  }

  updateBasePricePlay (event) {
    this.updateContentOwnership('basePricePlay', this.getValueForSwitch(event));
    if (!this.localContentOwnership.baseDurationPlay) this.updateContentOwnership('baseDurationPlay', 60);
  }

  showMonthlyYearlyInputs (property) {
    return this.localContentOwnership[property] !== null;
  }

  getValueForSwitch (e) {
    return (e.target.checked) ? 0 : null;
  }

  getValueForYearly (e) {
    return (e.target.checked) ? 'unset' : null;
  }

  updateContentOwnership (property, value) {
    this.$emit('edit:contentOwnership', property, value);
  }

  created () {
    if (this.contentOwnership.minutePrice && this.contentOwnership.minutePrice.current.price !== this.contentOwnership.basePriceMinute && this.contentOwnership.minutePrice.upcoming) {
      this.removeMinuteInstructions = moment().isAfter(this.contentOwnership.minutePrice.upcoming.startsAt);
    } else {
      this.removeMinuteInstructions = true;
    }
    if (this.contentOwnership.monthlyPrice && this.contentOwnership.monthlyPrice.current && this.contentOwnership.monthlyPrice.upcoming) {
      this.removeMonthlyInstructions = moment().isAfter(this.contentOwnership.monthlyPrice.upcoming.startsAt);
    } else {
      this.removeMonthlyInstructions = true;
    }
    if (this.contentOwnership.yearlyPrice && this.contentOwnership.yearlyPrice.current && this.contentOwnership.yearlyPrice.upcoming) {
      this.removeYearlyInstructions = moment().isAfter(this.contentOwnership.yearlyPrice.upcoming.startsAt);
    } else {
      this.removeYearlyInstructions = true;
    }
  }
}
</script>

<style scoped lang="postcss">
@import "core/shared/styles";

.price {
  & .price-group {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    grid-gap: var(--spacingMd);

    @media (--tablet) {
      grid-template-columns: repeat(2, 1fr);
    }

    & .instructions {
      & .label {
        @apply --f8;
      }

      & .value {
        @apply --f7;
      }
    }

    & .range {
      & .label {
        @apply --f8;
        margin-bottom: var(--spacingSm);
      }
    }

    & .label {
      margin-bottom: 1.5rem;
    }
  }

  & hr {
    margin: var(--spacingMd) 0;
  }

  & h1 {
    @apply --f9;

    color: var(--colorSBRedFlat);
  }

  & .price-play {
    margin-bottom: var(--spacingSm);
  }

  & .pricing {
    display: grid;
    grid-gap: var(--spacingMd);
    margin: var(--spacingMd) 0;
    align-items: start;

    &:last-child {
      margin-bottom: 0;
    }

    @media (--tablet) {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  & .instructions-grid {
    display: grid;
    grid-template-columns: auto 1fr;
    grid-column-gap: .5rem;
  }
  & .monthly-instructions,
  & .minute-instructions,
  & .yearly-instructions {
    @apply --f7;
    margin-top: var(--spacingXS);
  }
  & .warning {
    color: var(--colorErrorFlat)
  }

  & .alert-triangle {
    margin-top: var(--spacingXS);
    fill: var(--colorErrorFlat);
    width: 1.7rem;
  }
}
</style>
